import { useEffect } from 'react'

import { AxiosError } from 'axios'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'
import { formTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { useApi } from '@tabeeb/modules/shared/utils/API'

import { Form } from '../types'
import { FormsQueryKeys } from '../queries'

export interface UseFormsHookProps {
  skip?: number
  take?: number
  search?: string | null
  folderId?: number | null
  enabled?: boolean
}

const useForms = ({ skip = 0, take = 25, search = '', folderId = null, enabled = true }: UseFormsHookProps = {}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { mainApi: api } = useApi()

  const normalizedFolderId = folderId === 0 ? null : folderId

  const query = useQuery({
    queryKey: [FormsQueryKeys.Forms, skip, take, search, normalizedFolderId],
    queryFn: () =>
      api.forms.getAllAsync({
        Skip: skip,
        Take: take,
        Search: search ?? undefined,
        FolderId: normalizedFolderId ?? undefined,
      }),
    enabled,
  })

  const { isError, error: queryError } = query

  useEffect(() => {
    if (isError && queryError) {
      const error = queryError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isError, queryError, dispatch])

  const copyMutation = useMutation({
    mutationFn: (data: Form) => api.forms.copyAsync(data.FormId),
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format(`Failed to copy the form`) }))
    },
    onSuccess: () => {
      dispatch(
        onAddSuccessNotification({ message: formTitleFormatter.format(`Form has been copied to the Main List`) })
      )
      queryClient.invalidateQueries({ queryKey: [FormsQueryKeys.Forms] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (data: Form) => api.forms.deleteAsync(data.FormId),
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format(`Failed to delete the form`) }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: formTitleFormatter.format(`Form deleted successfully`) }))
      queryClient.invalidateQueries({ queryKey: [FormsQueryKeys.Forms] })
    },
  })

  const publishMutation = useMutation({
    mutationFn: (data: { formId: number; tenantsIds: number[] }) =>
      api.forms.publishAsync({ FormId: data.formId, TenantsIds: data.tenantsIds }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format(`Failed to publish the form`) }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: formTitleFormatter.format(`Form published successfully`) }))
      queryClient.invalidateQueries({ queryKey: [FormsQueryKeys.Forms] })
    },
  })

  const unpublishMutation = useMutation({
    mutationFn: (data: { formId: number; tenantsIds: number[] }) =>
      api.forms.unpublishAsync({ FormId: data.formId, TenantsIds: data.tenantsIds }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: formTitleFormatter.format(`Failed to unpublish the form`) }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: formTitleFormatter.format(`Form unpublished successfully`) }))
      queryClient.invalidateQueries({ queryKey: [FormsQueryKeys.Forms] })
    },
  })

  return {
    fetching: query.isFetching,
    loading: query.isLoading,
    total: query.data?.TotalCount,
    forms: query.data?.Items,
    onCopy: copyMutation.mutate,
    onPublish: publishMutation.mutate,
    onUnpublish: unpublishMutation.mutate,
    onDelete: deleteMutation.mutate,
    onReload: query.refetch,
  }
}

export default useForms
